/**
 * Pricing data transform.
 *
 * Bridges the Stripe-driven pricing response (pricing.json / GET /v1/pricing)
 * into the prop shapes the PricingPage composite and addon/bundle/upgrade
 * table components expect.
 *
 * When the API endpoint is live, the same transform applies.
 */
import type { PricingData, PricingTierData } from "../pricing";
import type { AddonRow } from "../pricing";
import type { BundleRow } from "../pricing";
import type { UpgradeRow } from "../pricing";

/* ── Raw API / fallback shapes ── */

interface RawPriceDetail {
  amount: number;
  display: string;
}

export interface RawTier {
  tier: string;
  name: string;
  description: string;
  recommended?: boolean;
  /** Sales-engaged (Enterprise/Sovereign): no public Stripe price. Rendered as a
   *  "Custom / Talk to us" card showing `highlights` instead of self-serve quota,
   *  and excluded from the auto-generated per-tier tabs. */
  salesEngaged?: boolean;
  /** Qualitative card points for a sales-engaged tier (shown in place of quota). */
  highlights?: string[];
  entitlements?: {
    integrations: number;
    records_per_month: number;
    storage_gb: number;
    workflows: number;
    ai_operations: number;
    environments: number;
    max_sync_mode: string;
    api_keys: number;
  };
  prices?: {
    gbp: { monthly: RawPriceDetail; quarterly: RawPriceDetail; annual: RawPriceDetail };
    eur: { monthly: RawPriceDetail; quarterly: RawPriceDetail; annual: RawPriceDetail };
    usd: { monthly: RawPriceDetail; quarterly: RawPriceDetail; annual: RawPriceDetail };
  };
}

interface RawPack {
  dimension: string;
  unit_label: string;
  prices: { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail };
}

interface RawBundle {
  label: string;
  per_gb_pence: number;
  saving_vs_base: string;
  prices: { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail };
}

interface RawUpgrade {
  label: string;
  prices: { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail };
}

interface RawSupport {
  label: string;
  prices: { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail };
}

interface RawModulePricing {
  module: string;
  name: string;
  description: string;
  prices_by_tier: Record<string, { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail }>;
}

interface RawStoragePack {
  pack: string;
  name: string;
  additional_gb: number;
  prices: { gbp: RawPriceDetail; eur: RawPriceDetail; usd: RawPriceDetail };
}

/** A single capability row in the shared feature matrix (pricing.json.features).
 *  `switch` rows carry booleans; `quantity` rows carry display strings. The
 *  `tiers` map is keyed by tier id and includes enterprise + sovereign. */
export interface RawFeatureRow {
  feature: string;
  key: string;
  type: "switch" | "quantity";
  tiers: Record<string, boolean | string>;
}

export interface RawFeatureCategory {
  category: string;
  rows: RawFeatureRow[];
}

export interface RawPricingResponse {
  tiers: RawTier[];
  packs: RawPack[];
  bundles: RawBundle[];
  upgrades: RawUpgrade[];
  support: RawSupport[];
  overage: RawOverageEntry[];
  features: RawFeatureCategory[];
  modules: RawModulePricing[];
  storage_packs: RawStoragePack[];
}

/** Ordered tier columns for the feature matrix, derived from the data — so a
 *  tier added in Stripe / pricing.json (e.g. enterprise, sovereign) appears as
 *  a column automatically. Self-serve names come from `tiers`; the rest are
 *  title-cased from the key. */
export function featureColumns(raw: RawPricingResponse): { key: string; label: string }[] {
  const order = ["pilot", "starter", "growth", "scale", "enterprise", "sovereign"];
  const present = new Set((raw.features ?? []).flatMap((c) => c.rows.flatMap((r) => Object.keys(r.tiers))));
  const nameByKey = new Map(raw.tiers.map((t) => [t.tier, t.name]));
  const ordered = order.filter((k) => present.has(k));
  // append any unexpected keys not in the canonical order, so nothing is dropped
  for (const k of present) if (!order.includes(k)) ordered.push(k);
  return ordered.map((k) => ({ key: k, label: nameByKey.get(k) ?? k.charAt(0).toUpperCase() + k.slice(1) }));
}

export interface ModuleRow {
  module: string;
  name: string;
  description: string;
  price: string;
  available: boolean;
}

export interface StoragePackRow {
  pack: string;
  name: string;
  additionalGb: number;
  price: string;
}

interface RawOverageEntry {
  dimension: string;
  unit_label: string;
  rates: { gbp: { amount: number; display: string }; eur: { amount: number; display: string }; usd: { amount: number; display: string } };
}

/* ── Sync mode display labels ── */
const SYNC_MODE_LABELS: Record<string, string> = {
  interval_15m: "Interval (15m)",
  near_realtime: "Near-realtime",
  realtime: "Realtime",
};

function formatRecords(n: number): string {
  if (n >= 1_000_000_000) return `${n / 1_000_000_000}B`;
  if (n >= 1_000_000) return `${n / 1_000_000}M`;
  if (n >= 1_000) return `${n / 1_000}k`;
  return String(n);
}

function formatStorage(gb: number): string {
  if (gb >= 1024) return `${(gb / 1024).toFixed(0)}TB`;
  return `${gb}GB`;
}

/* ── Transform: tiers → PricingData for composite ── */

const NULL_PRICES = {
  GBP: { monthly: null, quarterly: null, annual: null },
  EUR: { monthly: null, quarterly: null, annual: null },
  USD: { monthly: null, quarterly: null, annual: null },
};

export function tierToPricingTierData(raw: RawTier): PricingTierData {
  const e = raw.entitlements;
  // Sales-engaged tiers (Enterprise/Sovereign): custom price, qualitative
  // highlights, and a "Talk to us" CTA — no self-serve quota or checkout.
  if (raw.salesEngaged || !raw.prices) {
    return {
      id: raw.tier,
      name: raw.name,
      tag: raw.description,
      recommended: raw.recommended ?? false,
      ctaLabel: "Talk to us",
      ctaHref: "/contact?type=enterprise",
      features: (raw.highlights ?? []).map((label) => ({ label })),
      quota: [],
      prices: NULL_PRICES,
    };
  }
  return {
    id: raw.tier,
    name: raw.name,
    tag: raw.description,
    recommended: raw.recommended ?? false,
    ctaLabel: "Subscribe",
    // Opens the Join-the-pilot capture modal (any page) with the tier pre-set.
    ctaHref: `?join=pilot&tier=${raw.tier}`,
    features: [
      { label: "All features included" },
      { label: "Unlimited users" },
      { label: "Teams" },
      { label: "Full connector library" },
      { label: "Standard support" },
    ],
    quota: e ? [
      { label: "Active integrations", value: String(e.integrations) },
      { label: "Records/month", value: formatRecords(e.records_per_month) },
      { label: "Archival storage", value: formatStorage(e.storage_gb) },
      { label: "Workflows", value: String(e.workflows) },
      { label: "AI operations/month", value: String(e.ai_operations) },
      { label: "Max sync mode", value: SYNC_MODE_LABELS[e.max_sync_mode] ?? e.max_sync_mode },
    ] : [],
    prices: {
      GBP: { monthly: raw.prices.gbp.monthly.amount / 100, quarterly: raw.prices.gbp.quarterly.amount / 100, annual: raw.prices.gbp.annual.amount / 100 },
      EUR: { monthly: raw.prices.eur.monthly.amount / 100, quarterly: raw.prices.eur.quarterly.amount / 100, annual: raw.prices.eur.annual.amount / 100 },
      USD: { monthly: raw.prices.usd.monthly.amount / 100, quarterly: raw.prices.usd.quarterly.amount / 100, annual: raw.prices.usd.annual.amount / 100 },
    },
  };
}

export function toPricingPageData(raw: RawPricingResponse): PricingData {
  return { tiers: raw.tiers.map(tierToPricingTierData) };
}

/** The headline entitlements a tier-topology diagram annotates — integration
 *  fan-out, throughput, sync cadence, environments. Same single formatting
 *  source as the capacity grid (Rule 17). */
export function tierTopology(raw: RawTier): {
  integrations: string; records: string; syncMode: string; environments: string;
} {
  const e = raw.entitlements;
  if (!e) return { integrations: 'Custom', records: 'Custom', syncMode: 'Realtime', environments: 'Custom' };
  return {
    integrations: String(e.integrations),
    records: formatRecords(e.records_per_month),
    syncMode: SYNC_MODE_LABELS[e.max_sync_mode] ?? e.max_sync_mode,
    environments: String(e.environments),
  };
}

/** The full entitlement set for a tier, label+value, for the per-tier detail
 *  panel. Single formatting source (Rule 17) — no capacity numbers hardcoded
 *  in the marketing layer. */
export function tierCapacityRows(raw: RawTier): { label: string; value: string }[] {
  const e = raw.entitlements;
  if (!e) return [];
  return [
    { label: "Active integrations", value: String(e.integrations) },
    { label: "Records / month", value: formatRecords(e.records_per_month) },
    { label: "Archival storage", value: formatStorage(e.storage_gb) },
    { label: "Workflows", value: String(e.workflows) },
    { label: "AI operations / month", value: String(e.ai_operations) },
    { label: "Environments", value: String(e.environments) },
    { label: "API keys", value: String(e.api_keys) },
    { label: "Max sync mode", value: SYNC_MODE_LABELS[e.max_sync_mode] ?? e.max_sync_mode },
  ];
}

/* ── Transform: packs + overage → AddonRow[] ── */

type CurrencyKey = "gbp" | "eur" | "usd";

export function toAddonRows(packs: RawPack[], overage: RawOverageEntry[], currency: CurrencyKey = "gbp"): AddonRow[] {
  return packs.map((p) => {
    const ov = overage.find((o) => o.dimension === p.dimension);
    return {
      name: p.unit_label,
      packPrice: p.prices[currency].display,
      overageRate: ov ? ov.rates[currency].display : "—",
    };
  });
}

/* ── Transform: bundles → BundleRow[] ── */

export function toBundleRows(bundles: RawBundle[], currency: CurrencyKey = "gbp"): BundleRow[] {
  return bundles.map((b, i) => {
    const row: BundleRow = {
      name: "Archival storage",
      size: b.label,
      price: b.prices[currency].display,
      perGb: `${b.per_gb_pence}p/GB`,
      highlight: i === bundles.length - 1,
    };
    if (b.saving_vs_base) row.savings = b.saving_vs_base;
    return row;
  });
}

/* ── Transform: upgrades + support → UpgradeRow[] ── */

export function toUpgradeRows(upgrades: RawUpgrade[], support: RawSupport[], currency: CurrencyKey = "gbp"): UpgradeRow[] {
  const upgradeRows: UpgradeRow[] = upgrades.map((u) => ({
    name: u.label,
    adds: u.label,
    price: u.prices[currency].display,
  }));
  const supportRows: UpgradeRow[] = support.map((s) => ({
    name: s.label,
    adds: s.label,
    price: s.prices[currency].display,
  }));
  return [...upgradeRows, ...supportRows];
}

/* ── Transform: modules → ModuleRow[] ── */

export function toModuleRows(modules: RawModulePricing[], tier: string, currency: CurrencyKey = "gbp"): ModuleRow[] {
  return modules.map((m) => {
    const tierPricing = m.prices_by_tier[tier];
    if (tierPricing) {
      return {
        module: m.module,
        name: m.name,
        description: m.description,
        price: tierPricing[currency].display,
        available: true,
      };
    }
    return {
      module: m.module,
      name: m.name,
      description: m.description,
      price: "—",
      available: false,
    };
  });
}

/* ── Transform: storage_packs → StoragePackRow[] ── */

export function toStoragePackRows(storagePacks: RawStoragePack[], currency: CurrencyKey = "gbp"): StoragePackRow[] {
  return storagePacks.map((p) => ({
    pack: p.pack,
    name: p.name,
    additionalGb: p.additional_gb,
    price: p.prices[currency].display,
  }));
}
